import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Box, Button, Typography } from '@mui/material';

const NAV_LINKS = [
  { label: 'בית',              anchor: 'top' },
  { label: 'איך זה עובד',     anchor: '#features' },
  { label: 'רכיבים',          anchor: '#widgets' },
  { label: 'השוואה',           anchor: '#comparison' },
  { label: 'מסלולים',          anchor: '#pricing' },
  { label: 'שאלות ותשובות',    anchor: '#faq' },
  { label: 'צור קשר',          modal: 'contact' },
];

const NavBar = ({ onOpenModal }) => {
  const [scrolled, setScrolled] = useState(false);
  const [navVisible, setNavVisible] = useState(false);
  const [time, setTime] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 50);
      // Show nav links only after scrolling past the hero (185vh)
      setNavVisible(y > window.innerHeight * 1.75);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }));
    };
    update();
    const id = setInterval(update, 10000);
    return () => clearInterval(id);
  }, []);

  const handleNav = ({ anchor, modal, path }) => {
    if (modal) { onOpenModal?.(modal); return; }
    if (path)  { navigate(path); return; }
    if (anchor === 'top') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    document.querySelector(anchor)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: scrolled ? 'rgba(30,58,95,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.08)' : 'none',
        transition: 'background 0.3s ease, backdrop-filter 0.3s ease, border-color 0.3s ease',
        zIndex: 1300,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 }, minHeight: '64px !important' }}>

        {/* Logo */}
        <Box
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', flexShrink: 0 }}
        >
          <Box component="img" src="/inorai-logo.png" alt="inorai logo"
            sx={{ width: 56, height: 56, objectFit: 'contain' }}
          />
          <Typography sx={{ fontFamily: 'Secular One, sans-serif', fontSize: '1.3rem', color: 'white', letterSpacing: '0.04em' }}>
            inorai
          </Typography>
        </Box>

        {/* Nav links — desktop, only after hero */}
        <Box sx={{
          display: { xs: 'none', lg: 'flex' },
          alignItems: 'center',
          gap: 0,
          opacity: navVisible ? 1 : 0,
          pointerEvents: navVisible ? 'auto' : 'none',
          transition: 'opacity 0.4s ease',
        }}>
          {NAV_LINKS.map((link) => (
            <Button
              key={link.label}
              onClick={() => handleNav(link)}
              sx={{
                color: 'rgba(255,255,255,0.72)',
                fontFamily: 'Heebo, sans-serif',
                fontWeight: 500,
                fontSize: '0.83rem',
                px: 1.4,
                py: 0.5,
                minWidth: 0,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.07)' },
                transition: 'all 0.2s ease',
              }}
            >
              {link.label}
            </Button>
          ))}
        </Box>

        {/* Clock + CTA */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {time && (
            <Box sx={{
              px: 1.5, py: 0.5, borderRadius: 20,
              bgcolor: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.15)',
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
            }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontFamily: 'Rubik, sans-serif', direction: 'ltr' }}>
                {time}
              </Typography>
            </Box>
          )}

          <Button
            variant="outlined"
            onClick={() => navigate('/login')}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.4)',
              fontFamily: 'Heebo, sans-serif',
              fontWeight: 500,
              fontSize: '0.9rem',
              px: 2.5,
              py: 0.75,
              borderRadius: 2,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: '#9a7209', bgcolor: 'rgba(154,114,9,0.15)' },
              transition: 'all 0.2s ease',
            }}
          >
            כניסה למערכת
          </Button>
        </Box>

      </Toolbar>
    </AppBar>
  );
};

export default NavBar;
